import { Tooltip } from '@mui/material';
import {
  GridColumn as Column,
  GridCellProps,
} from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import warningIcon from 'Assets/Icons/warning.svg';
import { dueDateTimeCell } from 'Components/Grid/DisplayCells/CustomCells';
import { DatePickerFilterCell } from 'Components/Grid/FilterCells/DatePickerFilterCell';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { DropdownFilterCell } from 'Components/Grid/FilterCells/DropdownFilterCell';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';
import React, { HTMLProps, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DropdownDbOption } from 'v3/Components/Form/Dropdown';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import { draftReferralStatusCell } from 'v3/Components/Shared/StatusPill';
import {
  DateCellContent,
  EditDeleteCell,
  FilterBarMainGrid,
  LinkCell,
} from '../../Components/Grid';
import {
  FormProvider,
  GridProvider,
  useEnums,
  useFormState,
  useGlobalContext,
  useGridState,
} from '../../Context';
import './DraftReferrals.scss';

import {
  defaultGridProps,
  emptyGuid,
  FilterBarFilterType,
  FilterConfig,
  GridColumnWidth,
  IFilterConfig,
} from '../../Models';
import { Permission } from '../../Models/Templates/Permission/Permission';
import { ENDPOINTS } from '../../Models/Templates/Routes/Routes';
import { AddEditSubRoute, ClaimsSubRoute, ROUTES } from '../../Navigation';
import {
  buildEndpoint,
  buildRoute,
  DEFAULT_DATETIME24H_FORMAT,
  useApiWorker,
  useDataFetcher,
  useGridSearch,
} from '../../Utilities';
import { set } from 'lodash';
import { DateRangePickerFilterCell } from 'Components/Grid/FilterCells/DateRangePickerFilterCell';
import { date } from 'faker';

class ReferralsRequestSearch {
  searchKey?: string;
  id?: string;
  clientId?: string | null;
}

export enum ReferralsRequestGridIds {
  DetailedSearch = 'detailedSearch',
}

export const DraftReferrals: React.FC<any> = ({
  header = true,
  clientId = null,
  disableUrlSync = false,
}: {
  header?: boolean;
  clientId?: string | null;
  disableUrlSync?: boolean;
}) => {
  return (
    <>
      {header ? (
        <GridProvider entityName="Referral Request">
          <div className="main-grid-header">
            <h1>Referral Drafts</h1>
            <NewReferralButton />
          </div>
          <DraftReferralsRequestsFilteredGrid
            clientId={clientId}
            disableUrlSync={disableUrlSync}
          />
        </GridProvider>
      ) : (
        <DraftReferralsRequestsFilteredGrid
          clientId={clientId}
          disableUrlSync={disableUrlSync}
        />
      )}
    </>
  );
};

export type DraftReferralsRequestsFilteredGridProps =
  HTMLProps<DraftReferralsRequestsFilteredGridProps> & {
    id?: string;
    enableGridRefresh?: boolean;
    clientId?: string | null;
    disableUrlSync?: boolean;
  };

export const DraftReferralsRequestsFilteredGrid: React.FC<
  DraftReferralsRequestsFilteredGridProps
> = ({ id, clientId, disableUrlSync, ...props }) => {
  const referralSearch = new ReferralsRequestSearch();
  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      ' Name, SSN, or @'
    ),
    new FilterConfig(
      'dateOfBirth',
      FilterBarFilterType.DateRange,
      'Date Of Birth'
    ),
    new FilterConfig(
      'user',
      FilterBarFilterType.AsyncDropdown,
      'User',
      {
        readEndpoint: buildEndpoint(ENDPOINTS.users.base, 'getFiltered'),
      },
      undefined,
      true
    ),

    new FilterConfig(
      'id',
      FilterBarFilterType.SearchText,
      ' Name, ID, or #',
      undefined,
      () => (id ? id : '')
    ),

    new FilterConfig(
      'clientId',
      FilterBarFilterType.SearchText,
      ' Client ID',
      undefined,
      (value) => (clientId ? clientId : '')
    ),
  ];
  referralSearch.id = id;
  referralSearch.clientId = clientId;

  return (
    <FormProvider formModel={referralSearch}>
      <GridProvider entityName="Draft Referral Request">
        <DraftReferralsRequestsGridWithSelection
          id={id}
          clientId={clientId}
          enableGridRefresh={true}
          disableUrlSync={disableUrlSync}
        />
      </GridProvider>
    </FormProvider>
  );
};

type FilterModel = {
  services?: DropdownDbOption[];
  submittedBy?: DropdownDbOption[];
  subServices?: DropdownDbOption[];
};

const DraftReferralsRequestsGridWithSelection: React.FC<
  DraftReferralsRequestsFilteredGridProps
> = ({ id, clientId, disableUrlSync, ...props }) => {
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { lineOfBusiness } = useEnums();
  const history = useHistory();
  const API = useApiWorker('3.0');
  const { reloadGrid, setIsFetching } = useGridState();
  const [gridToggler] = useGridSearch();
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const dateColumnWidth = filtersEnabled ? '260px' : '200px';

  const [filtersModel, filtersStatus] = useDataFetcher<FilterModel>(
    `${ENDPOINTS.referralsRequestsDraft.base}/GetFiltersModel`,
    {},
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  const endpoint = ENDPOINTS.referralsRequestsDraft.read;

  function navigateToDraft(
    id: string,
    lob: string,
    clientId?: string,
    referrerId?: string
  ) {
    history.push(
      buildRoute(
        ROUTES.ADD_EDIT,
        AddEditSubRoute.ReferralsRequest,
        id +
          '?lob=' +
          lob +
          '&draft=1&clientId=' +
          clientId +
          (referrerId ? '&referrerId=' + referrerId : '')
      )
    );
  }

  function onRowClick(dataItem: any) {
    navigateToDraft(
      dataItem?.id,
      dataItem?.lineOfBusiness?.value,
      dataItem?.clientId,
      dataItem?.referralSourceId
    );
  }

  const selectedIdsRef = useRef<Set<string>>(new Set());
  const [selectedCount, setSelectedCount] = useState(0);

  const cellSettersRef = useRef<Record<string, (checked: boolean) => void>>({});

  const headerCheckSetterRef = useRef<(checked: boolean) => void>();

  const registerToggle = (id: string, checked: boolean) => {
    const set = selectedIdsRef.current;

    if (checked) {
      set.add(id);
    } else {
      set.delete(id);
      headerCheckSetterRef.current?.(false);
    }

    setSelectedCount(set.size);
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const ReferralDateAndTimeColumn: React.FC<GridCellProps> = (
    props: GridCellProps
  ) => {
    const item = props.dataItem;

    const rawDate = item?.referralDate;
    if (!rawDate) {
      return (
        <td className="cell--date-with-warning">
          <DateCellContent {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />
        </td>
      );
    }

    const referralDate = new Date(rawDate);
    const now = new Date();

    const MS_PER_DAY = 1000 * 60 * 60 * 24;
    const diffMs = now.getTime() - referralDate.getTime();
    const daysInactive = Math.floor(diffMs / MS_PER_DAY);

    const showWarning = daysInactive >= 5 && daysInactive < 30;
    const daysRemaining = 30 - daysInactive;

    const xLabel = daysInactive === 1 ? '1 day' : `${daysInactive} days`;
    const yLabel = daysRemaining === 1 ? '1 day' : `${daysRemaining} days`;

    const tooltipMessage = `This item hasn’t been active for ${xLabel}. Take action if you want to keep it.`;

    return (
      <td>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <DateCellContent {...props} dateFormat={DEFAULT_DATETIME24H_FORMAT} />
          {showWarning && (
            <Tooltip title={tooltipMessage} style={{ height: '100%' }}>
              <img
                src={warningIcon}
                alt="Inactive draft warning"
                style={{
                  paddingLeft: 10,
                  width: 28,
                  height: 28,
                  paddingBottom: 0.5,
                }}
              />
            </Tooltip>
          )}
        </div>
      </td>
    );
  };

  const handleDiscardDrafts = async () => {
    try {
      setDeleteLoading(true);
      setIsFetching(true);
      const ids = Array.from(selectedIdsRef.current);
      if (ids.length === 0) {
        setIsDeleteModalOpen(false);
        setIsFetching(false);
        return;
      }

      await Promise.all(
        ids.map((id) =>
          API.delete(`${ENDPOINTS.referralsRequestsDraft.base}/${id}`)
        )
      );

      selectedIdsRef.current = new Set();
      setSelectedCount(0);
      setIsDeleteModalOpen(false);
      reloadGrid();
    } catch (err) {
      console.error('Error deleting selected drafts:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const SelectionCell: React.FC<GridCellProps> = (props: GridCellProps) => {
    const id = props.dataItem?.id as string | undefined;
    const [checked, setChecked] = useState<boolean>(
      id ? selectedIdsRef.current.has(id) : false
    );

    React.useEffect(() => {
      if (!id) return;

      cellSettersRef.current[id] = (nextChecked: boolean) => {
        setChecked(nextChecked);
        registerToggle(id, nextChecked);
      };

      return () => {
        delete cellSettersRef.current[id];
      };
    }, [id]);

    if (!id) {
      return <td />;
    }

    return (
      <td onClick={(e) => e.stopPropagation()} style={{ width: 60 }}>
        <Checkbox
          checked={checked}
          onChange={(e) => {
            e.syntheticEvent?.stopPropagation?.();
            const next = !checked;
            setChecked(next);
            registerToggle(id, next);
          }}
        />
      </td>
    );
  };

  const SelectionHeaderCell: React.FC = () => {
    const [allChecked, setAllChecked] = useState(false);

    React.useEffect(() => {
      headerCheckSetterRef.current = setAllChecked;
      return () => {
        headerCheckSetterRef.current = undefined;
      };
    }, []);

    const handleToggleAll = (e: any) => {
      e?.syntheticEvent?.stopPropagation?.();
      const next = !allChecked;
      setAllChecked(next);

      const setters = cellSettersRef.current;
      Object.keys(setters).forEach((id) => {
        setters[id](next);
      });
    };

    return <Checkbox checked={allChecked} onChange={handleToggleAll} />;
  };

  return (
    <React.Fragment>
      <FilterBarMainGrid
        gridId="DraftReferrals"
        isMainGrid
        disableUrlSync={disableUrlSync}
        customHeaderSection={
          <>
            {selectedCount > 0 && (
              <div className="custom-header-section">
                <div
                  className="table-selection-banner"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span
                    style={{
                      color: '#000',
                      fontFamily: 'NotoSans, sans-serif',
                      fontSize: 14,
                      fontStyle: 'italic',
                      fontWeight: 400,
                      lineHeight: '16px',
                      marginRight: '1rem',
                    }}
                  >
                    You’ve selected {selectedCount} item
                    {selectedCount === 1 ? '' : 's'}
                  </span>
                  <button
                    style={{
                      color: 'var(--pimary-blue-600, #1162E3)',
                      fontVariantNumeric: 'lining-nums tabular-nums',
                      fontFeatureSettings: '"liga" off, "clig" off',
                      fontFamily: 'NotoSansBold, sans-serif',
                      fontSize: 14,
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '20px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={deleteLoading}
                  >
                    Delete Selected
                  </button>
                </div>
              </div>
            )}
          </>
        }
        headerTitle="Referral Drafts"
        refreshToggle={gridToggler}
        readEndpoint={endpoint}
        onRowClick={(e) => onRowClick(e.dataItem)}
        filterBarProps={{
          filterConfigurations: [],
          filterValues: { clientId },
          isColumnSelectorEnabled: false,
        }}
        hideFilterIcon={true}
        exportEndpoint={`${ENDPOINTS.referralsRequestsDraft.base}/exportToCSV`}
        exportEndpointVersion="3.0"
        apiVersion="3.0"
        enableGridRefresh={true}
        onFiltersEnabledChange={setFiltersEnabled}
        {...defaultGridProps}
        pageSize={10}
      >
        <Column
          field="_select"
          title=""
          headerCell={SelectionHeaderCell}
          cell={SelectionCell}
          filterable={false}
          width={40}
        />
        <Column
          field="referralDate"
          title="Referral Date & Time"
          cell={ReferralDateAndTimeColumn}
          filterable
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          width={dateColumnWidth}
          columnMenu={ColumnMenu}
        />
        <Column
          field="lineOfBusinessName"
          title="Line Of Business"
          cell={lineOfBusinessCell}
          filterable
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          width={dateColumnWidth}
          columnMenu={ColumnMenu}
        />
        <Column
          field="claimNumber"
          title="Claim/Client #"
          cell={claimLinkCell}
          filterable
          filterCell={DebouncedTextFilterCell}
          width={'180px'}
          columnMenu={ColumnMenu}
        />
        <Column
          field="name"
          title="Claimant Name"
          filterable
          filterCell={DebouncedTextFilterCell}
          width={'160px'}
          columnMenu={ColumnMenu}
        />

        <Column
          field="statusName"
          title="Status"
          cell={(e) =>
            draftReferralStatusCell({
              ...e,
              onClick: () => onRowClick(e.dataItem),
            })
          }
          filterable={false}
          width={'180px'}
          columnMenu={ColumnMenu}
        />
        <Column
          field="dueDate"
          title="Due Date & Time"
          cell={dueDateTimeCell}
          filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          width={dateColumnWidth}
          columnMenu={ColumnMenu}
        />
        <Column
          field="service"
          title="Service"
          filterable
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.services : []
            )
          }
          width={'140px'}
          columnMenu={ColumnMenu}
        />
        <Column
          field="subService"
          title="Sub Service"
          filterCell={(x) =>
            DropdownFilterCell(
              x,
              undefined,
              filtersStatus === 'success' ? filtersModel.subServices : []
            )
          }
          width={'140px'}
          columnMenu={ColumnMenu}
        />
        <Column
          field="recentUser"
          title="Recent User"
          filterable
          filterCell={DebouncedTextFilterCell}
          cell={(x) => pascalCaseCell(x.dataItem.recentUser)}
          width={'200px'}
          columnMenu={ColumnMenu}
        />

        <Column
          field="actions"
          title={' '}
          cell={ActionsCellEdit}
          filterable={false}
          sortable={false}
          width={'40px'}
        />
      </FilterBarMainGrid>

      {isDeleteModalOpen && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {}}
        >
          <div
            className="table-delete-modal"
            style={{
              background: 'white',
              borderRadius: 12,
              padding: '1.25rem',
              minWidth: 812,
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p
              style={{
                marginTop: 0,
                fontFamily: 'NotoSans, sans-serif',
                fontSize: 20,
                fontWeight: 700,
                lineHeight: '24px',
                color: '#071522',
                marginBottom: 28,
              }}
            >
              Are you sure you want to discard {selectedCount}{' '}
              {selectedCount === 1 ? 'draft' : 'drafts'}?
            </p>
            <p
              style={{
                marginBottom: '1rem',
                fontSize: 14,
                lineHeight: '20px',
                fontFamily: 'NotoSans, sans-serif',
              }}
            >
              You’ll lose all the information on these referrals.
            </p>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'flex-end',
              }}
            >
              <button
                style={{
                  width: 147,
                  height: 44,
                  borderRadius: 40,
                  background: 'transparent',
                  color: '#1165E3',
                  border: '2px solid #1165E3',
                  fontFamily: 'NotoSansBold, sans-serif',
                }}
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={deleteLoading}
              >
                Keep Drafts
              </button>
              <button
                style={{
                  width: 147,
                  height: 44,
                  borderRadius: 40,
                  background: '#C80404',
                  color: 'white',
                  border: 'none',
                  fontFamily: 'NotoSansBold, sans-serif',
                }}
                onClick={handleDiscardDrafts}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Discarding...' : 'Discard Drafts'}
              </button>
            </div>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

const pascalCaseCell = (text: string) => {
  if (!text) {
    return <td />;
  }

  return (
    <td>
      {text.replace(
        /\w+/g,
        (w) => w[0].toUpperCase() + w.slice(1).toLowerCase()
      )}
    </td>
  );
};

const claimLinkCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return props.dataItem.claimId ? (
    <LinkCell
      {...props}
      linkField={props.dataItem.claimantId}
      endpoint={buildRoute(
        ROUTES.CLAIM,
        props.dataItem.claimId,
        ClaimsSubRoute.View
      )}
    />
  ) : (
    <td>{props.dataItem.claimNumber}</td>
  );
};

const ActionsCellEdit: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <EditDeleteCell
      apiVersion="3.0"
      deleteEndpoint={ENDPOINTS.referralsRequestsDraft.base}
      deletePermissions={[Permission.DeleteReferralsRequestDraft]}
      {...props}
      field="id"
    />
  );
};

const lineOfBusinessCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <td>{props.dataItem.lineOfBusiness?.displayName}</td>;
};
